"use client";

import { type MouseEvent } from "react";
import Image from "next/image";
import {
  Button,
  Skeleton,
  Tooltip,
} from "@boostdev/design-system-components/client";
import { InfoIcon } from "@/components/atoms/icons/InfoIcon";
import type { Photo } from "@/lib/immich/types";
import { LiveThumbnailVideo } from "./LiveThumbnailVideo";
import css from "./PhotoThumbnail.module.css";

export interface PhotoThumbnailProps {
  readonly photo: Photo;
  readonly index: number;
  readonly onClick?: (index: number) => void;
  /** Localized label for the small "LIVE" badge shown on Live Photos. */
  readonly liveBadgeLabel?: string;
  /** Localized label for the small "VIDEO" badge shown on standalone videos. */
  readonly videoBadgeLabel?: string;
}

const MASONRY_SIZES = "(min-width: 64rem) 25vw, (min-width: 40rem) 33vw, 50vw";

export function PhotoThumbnail({
  photo,
  index,
  onClick,
  liveBadgeLabel,
  videoBadgeLabel,
}: PhotoThumbnailProps) {
  const handleClick = (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    onClick?.(index);
  };

  const description = photo.exif.description?.trim();

  return (
    <div className={css.thumbnailWrap}>
      <button
        type="button"
        className={css.thumbnail}
        onClick={handleClick}
        aria-label={photo.alt}
      >
        <span className={css.skeleton} aria-hidden="true">
          <Skeleton />
        </span>
        <Image
          src={photo.previewUrl}
          alt={photo.alt}
          width={photo.width}
          height={photo.height}
          loading="lazy"
          sizes={MASONRY_SIZES}
          className={css.image}
        />
        {photo.livePhotoUrl ? (
          <LiveThumbnailVideo
            src={photo.livePhotoUrl}
            photoId={photo.id}
            badgeLabel={liveBadgeLabel ?? "LIVE"}
          />
        ) : null}
        {photo.videoUrl ? (
          <span className={css.videoBadge}>{videoBadgeLabel ?? "VIDEO"}</span>
        ) : null}
      </button>
      {description ? (
        <Tooltip
          className={css.infoBadge}
          placement="bottom"
          content={<span className={css.tooltipContent}>{description}</span>}
        >
          <Button
            isIconOnly
            aria-label={description}
            onClick={() => onClick?.(index)}
            className={css.infoButton}
          >
            <InfoIcon />
          </Button>
        </Tooltip>
      ) : null}
    </div>
  );
}
